/**
 * Locale-aware formatting on top of Intl: numbers, dates, plural forms (Intl.PluralRules) and an ICU-like
 * message syntax for UI strings: `{n, plural, one {# drawing} other {# drawings}}` (`=0 {…}` exact matches too;
 * `#` = the formatted number). Plain `{name}` placeholders are filled afterwards (text.ts `fill`).
 */
import { intlLocale, type Locale } from './locales';

const nf = new Map<string, Intl.NumberFormat>();
const pr = new Map<string, Intl.PluralRules>();

export function formatNumber(locale: Locale, n: number, opts?: Intl.NumberFormatOptions): string {
  const key = `${locale}|${JSON.stringify(opts ?? {})}`;
  let f = nf.get(key);
  if (!f) nf.set(key, (f = new Intl.NumberFormat(intlLocale(locale), opts)));
  return f.format(n);
}

export function formatDate(locale: Locale, d: Date | string | number, opts?: Intl.DateTimeFormatOptions, overrides?: Partial<Record<Locale, string>>): string {
  return new Intl.DateTimeFormat(intlLocale(locale, overrides), opts).format(d instanceof Date ? d : new Date(d));
}

export type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string; [exact: `=${number}`]: string };

/** Pick the plural form of `n` for the locale (exact `=n` first), with `#` → the formatted number. */
export function plural(locale: Locale, n: number, forms: PluralForms): string {
  let rules = pr.get(locale);
  if (!rules) pr.set(locale, (rules = new Intl.PluralRules(intlLocale(locale))));
  const exact = forms[`=${n}` as `=${number}`];
  const s = exact ?? forms[rules.select(n)] ?? forms.other;
  return s.replace(/#/g, formatNumber(locale, n));
}

/** Expand ICU-like `{var, plural, …}` blocks (one level of nested `{name}` inside a form is allowed). */
export function formatPlurals(template: string, vars: Record<string, string | number>, locale: Locale): string {
  if (!template.includes(', plural,')) return template;
  return template.replace(/\{(\w+),\s*plural,\s*((?:[^{}]*\{(?:[^{}]|\{[^{}]*\})*\})+)\s*\}/g, (m, name: string, body: string) => {
    const n = Number(vars[name]);
    if (!Number.isFinite(n)) return m;
    const forms: Record<string, string> = {};
    const re = /(=?\w+)\s*\{((?:[^{}]|\{[^{}]*\})*)\}/g;
    let f: RegExpExecArray | null;
    while ((f = re.exec(body))) forms[f[1]!] = f[2]!;
    if (!forms.other) return m;
    return plural(locale, n, forms as PluralForms);
  });
}
